import { resolveTargetUrl, type TargetRecord } from './resolveTargetUrl';

/**
 * Minimal shape shared by ButtonBlock and LinkBlock records:
 * an optional record `target` and an optional uploaded `file`.
 */
export type CtaRecord = {
	target?: TargetRecord | null;
	file?: { url: string; filename: string } | null;
};

export type ResolvedCtaLink = {
	url: string | null;
	/** True when the CTA points to an uploaded file to download. */
	isDownload: boolean;
	/** True when the CTA points to an external URL (should open in a new tab). */
	isExternal: boolean;
};

/**
 * Builds a URL that forces the browser to download a DatoCMS asset.
 * DatoCMS assets are served cross-origin, where the `download` attribute is ignored:
 * the `dl` param makes the CDN respond with `Content-Disposition: attachment`.
 */
export function buildFileDownloadUrl(file: { url: string; filename: string }): string {
	const url = new URL(file.url);
	url.searchParams.set('dl', file.filename);
	return url.toString();
}

/**
 * Resolves a ButtonBlock / LinkBlock CTA. When a file is set it takes precedence
 * over the target, so clicking the CTA downloads the file.
 *
 * @param cta - The unmasked ButtonBlock or LinkBlock data.
 * @param currentPathname - Forwarded to `resolveTargetUrl` for context-dependent targets.
 */
export function resolveCtaLink(cta: CtaRecord, currentPathname: string = ''): ResolvedCtaLink {
	if (cta.file?.url) {
		return { url: buildFileDownloadUrl(cta.file), isDownload: true, isExternal: false };
	}

	if (cta.target) {
		return {
			url: resolveTargetUrl(cta.target, currentPathname),
			isDownload: false,
			isExternal: cta.target.__typename === 'ExternalLinkRecord',
		};
	}

	return { url: null, isDownload: false, isExternal: false };
}
